import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Heart, Star, Sparkles, Cloud, Smile } from 'lucide-react';
import { sampleCharacterTraits } from '../data/sampleStory.js';

// Trait chip colors - Storybook theme (blues/purples palette)
const traitColors = {
  personality: { bg: 'bg-[#E8E4F3]', text: 'text-[#9B8AC4]', border: 'border-[#9B8AC4]' },
  fears: { bg: 'bg-[#E3E8F5]', text: 'text-[#8B9FD9]', border: 'border-[#8B9FD9]' },
  dreams: { bg: 'bg-[#E5F4F5]', text: 'text-[#7EBDC3]', border: 'border-[#7EBDC3]' },
  likes: { bg: 'bg-[#F5EBF3]', text: 'text-[#D4A5C9]', border: 'border-[#D4A5C9]' },
};

const traitIcons = {
  personality: Smile,
  fears: Cloud,
  dreams: Star,
  likes: Heart,
};

/**
 * CharacterBuilderTeaser - Animated preview of character customization
 */
export default function CharacterBuilderTeaser() {
  const [activeTraits, setActiveTraits] = useState({
    personality: 0,
    fears: 0,
    dreams: 0,
    likes: 0,
  });

  // Cycle through traits for animation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTraits((prev) => ({
        personality: (prev.personality + 1) % sampleCharacterTraits.personality.length,
        fears: (prev.fears + 1) % sampleCharacterTraits.fears.length,
        dreams: (prev.dreams + 1) % sampleCharacterTraits.dreams.length,
        likes: (prev.likes + 1) % sampleCharacterTraits.likes.length,
      }));
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-16 section-lavender overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="section-header mb-4">Create Characters They'll Love</h2>
          <p className="text-[#5C534A] text-lg max-w-2xl mx-auto">
            Deep character customization lets you create truly personalized stories that reflect your child.
          </p>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Character Card Preview */}
          <div className="lg:w-2/5 flex justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Character Card */}
              <div className="w-64 sm:w-72 storybook-card p-6">
                {/* Avatar */}
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#9B8AC4] to-[#E8A0B4] flex items-center justify-center shadow-lg">
                  <User className="w-12 h-12 text-white" />
                </div>

                {/* Name Placeholder */}
                <div className="text-center mb-4">
                  <div className="h-6 bg-[#F5EDE3] rounded-lg w-32 mx-auto mb-2 flex items-center justify-center text-[#8B8279] text-sm">
                    Your Child's Name
                  </div>
                  <div className="text-sm text-[#8B8279]">Age 7</div>
                </div>

                {/* Current Traits Display */}
                <div className="space-y-2">
                  {Object.entries(sampleCharacterTraits).map(([category, traits]) => {
                    const colors = traitColors[category];
                    const currentTrait = traits[activeTraits[category]];
                    const IconComponent = traitIcons[category];

                    return (
                      <div key={category} className="flex items-center gap-2">
                        <IconComponent className={`w-4 h-4 ${colors.text}`} />
                        <AnimatePresence mode="wait">
                          <motion.span
                            key={currentTrait}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className={`px-2 py-0.5 rounded-full text-sm ${colors.bg} ${colors.text}`}
                          >
                            {currentTrait}
                          </motion.span>
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Floating Decorative Elements */}
              <motion.div
                className="absolute -top-4 -right-4 w-8 h-8 text-[#7B68EE]"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles className="w-full h-full" />
              </motion.div>
              <motion.div
                className="absolute -bottom-2 -left-4 w-6 h-6 text-[#9B8AC4]"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Star className="w-full h-full" />
              </motion.div>
            </motion.div>
          </div>

          {/* Feature Description */}
          <div className="lg:w-3/5">
            <div className="space-y-6">
              {/* Trait Categories */}
              {[
                {
                  icon: Smile,
                  title: 'Personality Traits',
                  description: 'Choose from brave, curious, kind, creative, and more to shape their character.',
                  category: 'personality',
                },
                {
                  icon: Cloud,
                  title: 'Fears & Challenges',
                  description: 'Add realistic fears like thunder, the dark, or being alone - stories help them cope.',
                  category: 'fears',
                },
                {
                  icon: Star,
                  title: 'Dreams & Aspirations',
                  description: 'What does your child dream of? Flying, being a hero, making friends?',
                  category: 'dreams',
                },
                {
                  icon: Heart,
                  title: 'Likes & Interests',
                  description: 'Include their favorite things - animals, activities, food - for true personalization.',
                  category: 'likes',
                },
              ].map((item, index) => {
                const colors = traitColors[item.category];
                const IconComponent = item.icon;

                return (
                  <motion.div
                    key={item.category}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex gap-4"
                  >
                    <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                      <IconComponent className={`w-5 h-5 ${colors.text}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#3D3832] mb-1">{item.title}</h3>
                      <p className="text-[#5C534A] text-sm">{item.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Floating Trait Chips - Desktop Only */}
            <div className="hidden lg:block mt-8 relative h-12">
              <AnimatePresence>
                {Object.entries(sampleCharacterTraits).map(([category, traits], catIndex) => {
                  const colors = traitColors[category];
                  return traits.map((trait, traitIndex) => {
                    const isActive = activeTraits[category] === traitIndex;
                    if (!isActive) return null;

                    return (
                      <motion.span
                        key={`${category}-${trait}`}
                        initial={{ opacity: 0, scale: 0.5, x: -20 }}
                        animate={{ opacity: 1, scale: 1, x: catIndex * 100 }}
                        exit={{ opacity: 0, scale: 0.5, x: 20 }}
                        className={`absolute px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text} ${colors.border} border shadow-sm`}
                        style={{ left: `${catIndex * 25}%` }}
                      >
                        {trait}
                      </motion.span>
                    );
                  });
                })}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
